import { Layout, Tag, Tooltip } from 'antd';
import {
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  AuditOutlined,
  UserOutlined,
  LogoutOutlined,
  TagOutlined,
  CalculatorOutlined,
  GlobalOutlined,
  FileTextOutlined,
  CopyOutlined,
  ScanOutlined
} from '@ant-design/icons';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import useInternalAdminAuth from '../contexts/InternalAdminAuthContext';
import { useAppNotifications } from '../utils/notify';

const { Sider, Content, Header } = Layout;

// Stage 5N-2C: improved internal-admin shell.
//
// Sidebar has three labelled sections (REVIEW / OPERATIONS / ADMIN) so
// the operator scans the nav structure rather than reading a flat
// list. Active item gets a left-edge accent rail + bold white text;
// inactive items lighten on hover.
//
// Logout is now a styled button (rose accent on hover) rather than the
// previous understated link. Topbar shows admin email + role chip
// without an extra dropdown — sign-out is in the sidebar and on the
// profile page.

function NavRow({ to, icon, label, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all',
          isActive
            ? 'bg-white/10 text-white font-semibold'
            : 'text-slate-300 hover:bg-white/5 hover:text-white'
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full transition-opacity ${
              isActive ? 'bg-brand-400 opacity-100' : 'opacity-0'
            }`}
            aria-hidden="true"
          />
          <span className="text-base shrink-0">{icon}</span>
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavSection({ title, children }) {
  return (
    <div className="mt-1">
      <p className="px-3 mt-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {title}
      </p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

export default function InternalAdminLayout() {
  const { admin, logout, hasPermission } = useInternalAdminAuth();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useAppNotifications();

  async function onLogout() {
    try {
      await logout();
      notifySuccess('Signed out');
    } catch (err) {
      notifyError(err, { title: 'Could not sign out cleanly' });
    } finally {
      navigate('/internal/login', { replace: true });
    }
  }

  return (
    <Layout className="min-h-screen">
      <Sider
        width={240}
        breakpoint="lg"
        className="!bg-slate-900"
        style={{ position: 'sticky', top: 0, height: '100vh' }}
      >
        {/* Brand block */}
        <div className="px-4 py-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 text-white shadow-lg shadow-brand-900/30">
              <SafetyCertificateOutlined />
            </span>
            <div className="leading-tight">
              <p className="text-white text-base font-bold m-0">Glass-i</p>
              <p className="text-slate-400 text-[11px] uppercase tracking-wider m-0">
                Internal Admin
              </p>
            </div>
          </div>
        </div>

        {/* Nav sections */}
        <nav className="px-3 pt-2 pb-24 overflow-y-auto h-[calc(100vh-160px)]">
          <NavSection title="Review">
            <NavRow
              to="/internal/accounts/pending"
              icon={<ClockCircleOutlined />}
              label="Pending Accounts"
            />
            <NavRow
              to="/internal/accounts/approved"
              icon={<CheckCircleOutlined />}
              label="Approved Accounts"
            />
            <NavRow
              to="/internal/accounts/rejected"
              icon={<CloseCircleOutlined />}
              label="Rejected Accounts"
            />
            {/* Stage 5N-12: SEZ pending signup admin queue. Distinct
                surface from /accounts/* because SEZ approval gates
                payment BEFORE a tenant exists — these rows live in
                pending_signups, not tenants. */}
            <NavRow
              to="/internal/sez-pending"
              icon={<GlobalOutlined />}
              label="SEZ Pending Approval"
            />
          </NavSection>

          <NavSection title="Operations">
            <NavRow
              to="/internal/accounts/suspended"
              icon={<PauseCircleOutlined />}
              label="Suspended Accounts"
            />
            <NavRow
              to="/internal/custom-plans"
              icon={<TagOutlined />}
              label="Custom Plans"
            />
            <NavRow
              to="/internal/pricing"
              icon={<CalculatorOutlined />}
              label="Pricing &amp; Tax"
            />
            <NavRow
              to="/internal/reports/definitions"
              icon={<FileTextOutlined />}
              label="Report Engine"
            />
            {hasPermission && hasPermission('tenants.clone_config') && (
              <NavRow
                to="/internal/tenant-clone"
                icon={<CopyOutlined />}
                label="Clone Tenant Config"
              />
            )}
            {/* Stage SCAN-1 — ESP32 scanner hardware inventory + allocation.
                Visible to every staff role that can read the fleet; the page
                itself hides the mint/rotate actions without
                scan_devices.manage. */}
            {hasPermission && hasPermission('scan_devices.view') && (
              <NavRow
                to="/internal/scan-devices"
                icon={<ScanOutlined />}
                label="Scanner Inventory"
              />
            )}
            <NavRow
              to="/internal/audit"
              icon={<AuditOutlined />}
              label="Audit Logs"
            />
          </NavSection>

          <NavSection title="Admin">
            <NavRow
              to="/internal/profile"
              icon={<UserOutlined />}
              label="Admin Profile"
            />
          </NavSection>
        </nav>

        {/* Sign-out — pinned bottom */}
        <div className="absolute bottom-0 left-0 right-0 px-3 py-3 border-t border-white/5 bg-slate-900">
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-100 bg-white/5 hover:bg-rose-500/15 hover:text-rose-300 active:bg-rose-500/25 transition-colors"
          >
            <LogoutOutlined />
            <span>Sign out</span>
          </button>
          {admin ? (
            <p className="text-[10px] text-slate-500 mt-2 mb-0 text-center truncate">
              {admin.email}
            </p>
          ) : null}
        </div>
      </Sider>

      <Layout className="bg-surface-50 min-w-0">
        <Header className="!bg-white !px-6 !h-14 flex items-center justify-between border-b border-surface-200 sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-ink-900 truncate">
              Glass-i Internal Admin
            </span>
            <span className="hidden sm:inline text-xs text-ink-400">
              / review &amp; approval
            </span>
          </div>

          <div className="flex items-center gap-3">
            {admin ? (
              <>
                <Tooltip title={admin.email} mouseEnterDelay={0.3}>
                  <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-100 text-sm">
                    <UserOutlined className="text-ink-400" />
                    <span className="font-medium text-ink-800 max-w-[180px] truncate">
                      {admin.displayName || admin.email}
                    </span>
                  </span>
                </Tooltip>
                <Tag color={admin.role === 'super_admin' ? 'red' : 'blue'}>
                  {admin.role}
                </Tag>
              </>
            ) : null}
          </div>
        </Header>

        <Content className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] w-full mx-auto min-w-0">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
